import 'react';
import {Box, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {L4Page} from '@/pages/components/audit-program/l-section/constants';
import {L4_PAGE} from '@/pages/components/audit-program/l-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface L4SearchViewProps {
    header: AuditProgramSectionVO;
    page: L4Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

/** L4 — Search for unrecorded liabilities (objective / method / result / conclusion). */
export default function L4SearchView({
    header,
    page,
    refCode = 'L4',
    values,
    onChange,
}: L4SearchViewProps) {
    const meta = {
        title: page.title || L4_PAGE.title,
        objectiveLabel: page.objectiveLabel || L4_PAGE.objectiveLabel,
        objectivePlaceholder: page.objectivePlaceholder || L4_PAGE.objectivePlaceholder,
        methodLabel: page.methodLabel || L4_PAGE.methodLabel,
        methodPlaceholder: page.methodPlaceholder || L4_PAGE.methodPlaceholder,
        resultLabel: page.resultLabel || L4_PAGE.resultLabel,
        resultPlaceholder: page.resultPlaceholder || L4_PAGE.resultPlaceholder,
        conclusionLabel: page.conclusionLabel || L4_PAGE.conclusionLabel,
        conclusionPlaceholder: page.conclusionPlaceholder || L4_PAGE.conclusionPlaceholder,
        remarksTitle: page.remarksTitle || L4_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : L4_PAGE.remarks,
    };

    const blocks: Array<{key: string; label: string; placeholder: string; minRows: number}> = [
        {key: 'objective', label: meta.objectiveLabel, placeholder: meta.objectivePlaceholder, minRows: 2},
        {key: 'method', label: meta.methodLabel, placeholder: meta.methodPlaceholder, minRows: 3},
        {key: 'result', label: meta.resultLabel, placeholder: meta.resultPlaceholder, minRows: 3},
        {key: 'conclusion', label: meta.conclusionLabel, placeholder: meta.conclusionPlaceholder, minRows: 2},
    ];

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Stack spacing={2} sx={{mb: 2, maxWidth: 820}}>
                {blocks.map((b) => (
                    <Box key={b.key}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                            {b.label}
                        </Typography>
                        <InputField
                            type="text"
                            fullWidth
                            compact
                            multiline
                            minRows={b.minRows}
                            placeholder={b.placeholder}
                            value={values[b.key] ?? ''}
                            onChange={(e) => onChange(b.key, e.target.value)}
                        />
                    </Box>
                ))}
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 44, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
